/**
 * Object behaviours: the gameplay rules that sit behind the palette.
 *
 * `objects.ts` is pure data — it says what a thing *is*. This file says what a
 * thing *does*: a `?` block pays out exactly once, a brick smashes when big
 * Tavi hits it, a switch flips every On/Off block in the course, a spring
 * catches a falling player and throws them back into the sky.
 *
 * Two conventions keep this file honest:
 *
 *   - **All per-instance bookkeeping lives in `ctx.state`.** The context is
 *     rebuilt from scratch every frame, so a module-level variable would be
 *     shared by every copy of the object in the level. `state` is the one
 *     object that persists for a single placement.
 *   - **Hooks are effects, `update` is state.** `onHeadBump` / `onSideHit` /
 *     `onStomp` fire from the player controller at the instant of contact, so
 *     they must be idempotent and cheap. Anything that has to be remembered
 *     over time (a saw's position, a switch's cooldown) belongs in `update`.
 *
 * The renderer reads this state back out, so the flag names below are a
 * contract with `engine/visuals.ts`: `spent` (used `?` block), `revealed` (a
 * hidden block that has been hit and stays on screen), `collected` (eaten
 * pickup), `taken` (raised flag), `on` (On/Off block appearance), `pressed`
 * (depressed switch), `compress` (squashed spring), `firing` (cannon flash) and
 * `spin` (saw rotation).
 *
 * Three keys are *not* part of that contract: `warned` is this file's own record
 * of which bad prop on this placement has already been reported, so a per-frame
 * rule cannot print sixty lines a second (`warnOnce`); `rearm` and `uses` are
 * the per-placement bookkeeping of a counted block — when it may pay again and
 * how many payouts it has left. See `payBlock`.
 */

import { sfxCheckpoint, sfxCoin, sfxPower } from '../core/audio'
import { TILE } from '../engine/constants'
import { spawnHudPopup } from '../engine/hud'
import { PHYS } from '../engine/physics'
import { blockUseCount, MAX_BLOCK_CONTENTS } from './objects'
import { SOLID_TERRAIN, Terrain } from './types'
import type {
  BehaviourContext,
  LevelRuntime,
  ObjectBehaviour,
  PlayerLike,
} from './types'

// ── the level surface behaviours actually use ───────────────────────────

/**
 * The slice of a placed object a behaviour needs in order to find *itself*.
 *
 * `BehaviourContext` deliberately carries no back-reference to the object —
 * only the raw `state` bag. Recovering the placement is done by identity: the
 * level hands every object the very same `state` object each frame, so the
 * entry whose `state === ctx.state` is the one being ticked.
 */
interface ObjectLike {
  index: number
  tx: number
  ty: number
  def: {
    id: string
    w: number
    h: number
    hitbox?: { w: number; h: number; ox?: number; oy?: number }
    /** Registry defaults, which is what an unusable authored prop falls back to. */
    props?: Record<string, number | string | boolean>
  }
  props: Record<string, number | string | boolean>
  state: Record<string, unknown>
  ox: number
  oy: number
  alive: boolean
}

/**
 * The `Level` methods `LevelRuntime` does not declare but the runtime always
 * provides: score, terrain edits, checkpoints and the global On/Off switch.
 * `stats` is optional because only the real `Level` carries the counter block.
 */
interface LevelLike {
  addScore(n: number): void
  setTerrain(tx: number, ty: number, t: number): void
  terrainAt(tx: number, ty: number): number
  setCheckpoint(tx: number, ty: number): void
  onOffActive: boolean
  objects: ObjectLike[]
  placementIndexFor(o: ObjectLike): number
  stats?: { coins: number; lives: number; score: number; time: number }
}

type LevelSurface = LevelRuntime & Partial<LevelLike>

/**
 * Optional player capabilities the concrete controller adds on top of
 * `PlayerLike`: fire/prism power, invulnerability queries and the duck input.
 * Every one is probed rather than assumed, so a behaviour still does something
 * sensible against a bare `PlayerLike`.
 */
interface PlayerHooks {
  input?: { down?: boolean }
  intent?: { down?: boolean }
  makeFire?(): void
  grantShield?(frames: number): void
  isShielded?: boolean
  isInvulnerable?: boolean
  shieldTime?: number
  invuln?: number
  lives?: number
  state?: string
}

// ── small shared helpers ────────────────────────────────────────────────

/** An axis-aligned world rectangle. */
interface Aabb {
  x: number
  y: number
  w: number
  h: number
}

/** True if two rectangles touch with positive area. */
function overlaps(a: Aabb, b: Aabb): boolean {
  return (
    a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y
  )
}

/** Read a number out of an `unknown` bag, falling back when absent. */
function num(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback
}

/** The level, seen through the wider surface the runtime provides. */
function lvl(ctx: BehaviourContext): LevelSurface {
  return ctx.level as LevelSurface
}

/** Locate the placed object this context belongs to. */
function self(ctx: BehaviourContext): ObjectLike | null {
  const objects = lvl(ctx).objects
  if (objects === undefined) return null
  for (const o of objects) {
    if (o.state === ctx.state) return o
  }
  return null
}

/**
 * The placement index of the calling object, or -1 when it cannot be found.
 * `Level.removeObject` takes this index, so self-removal is a one-liner.
 */
function ownIndex(ctx: BehaviourContext): number {
  const o = self(ctx)
  return o === null ? -1 : (lvl(ctx).placementIndexFor?.(o) ?? o.index)
}

/**
 * Say something about a placement exactly once, however many frames it lives.
 *
 * The latch is not an optimisation. A travelling object's props are read on
 * every frame it is on screen and a switch block is re-evaluated every time the
 * switch is thrown, so an unlatched report is up to 60 lines a second — worse
 * than no report at all, because it drowns the console the one time something
 * else needs to be read in it. The latch lives in the placement's own `state`
 * bag, so a course with thirty saws reports each bad prop once rather than once
 * per frame.
 */
function warnOnce(ctx: BehaviourContext, key: string, message: string): void {
  const reported = (ctx.state.warned ?? {}) as Record<string, boolean>
  if (reported[key] === true) return
  reported[key] = true
  ctx.state.warned = reported
  console.warn(message)
}

/**
 * Report a placement prop that is present but unusable.
 *
 * This exists because of the shape of bug it is aimed at, not because warnings
 * are fashionable. A prop is accepted by the placement type, stored, merged,
 * round-tripped through a save and drawn in the editor — and if the reader then
 * discards it in favour of a default, the designer has no way to find out. The
 * Wisp that faced the wrong way was the loud version of this; a mistyped
 * `contains` or a negative `range` is the quiet one, because the fallback is a
 * perfectly reasonable-looking value rather than an obviously broken one.
 *
 * It goes to the console rather than to a level-visible surface because the
 * level is not the place a designer is looking when the mistake is already made,
 * and it is the same channel `level.ts` uses for the other load-time authoring
 * faults (an unknown object id, a missing entity factory). `expected` is only
 * appended for a value that has a closed set of legal spellings.
 */
function warnBadProp(
  ctx: BehaviourContext,
  o: ObjectLike,
  key: string,
  value: unknown,
  expected?: string,
): void {
  const want = expected === undefined ? '' : ` (expected one of: ${expected})`
  warnOnce(
    ctx,
    key,
    `behaviour: ${o.def.id} at ${o.tx},${o.ty} has prop ${key}=${JSON.stringify(
      value,
    )}, which is not usable${want}; using the default instead`,
  )
}

/** World rectangle of a placed object, honouring its pixel hitbox inset. */
function boxOf(o: ObjectLike): Aabb {
  const x = o.tx * TILE + o.ox
  const y = o.ty * TILE + o.oy
  const hb = o.def.hitbox
  if (hb === undefined) return { x, y, w: o.def.w * TILE, h: o.def.h * TILE }
  return { x: x + (hb.ox ?? 0), y: y + (hb.oy ?? 0), w: hb.w, h: hb.h }
}

/**
 * The y of the face a player *stands on* for this object — the top of its
 * solidity footprint, which is not the top of its pixel hitbox.
 *
 * The two differ whenever an object is drawn and collides as less than its
 * tile, which is most of the interesting ones. A spring's hitbox is the lower
 * 8px of its tile (the coil), so its hitbox top sits 8px *below* the surface
 * the player is actually resting on. Triggering off the band therefore measured
 * an 8px gap for a player who was simply standing there, and no landing could
 * ever fall inside the window: the spring was inert. Solid tiles are baked from
 * `def.w` x `def.h` in `Level.mergeSolidity`, so the standing face is the top
 * of that footprint, not the top of the inset band.
 */
function surfaceY(o: ObjectLike): number {
  return o.ty * TILE + o.oy
}

/** The player's world rectangle. */
function playerBox(p: PlayerLike): Aabb {
  return { x: p.x, y: p.y, w: p.w, h: p.h }
}

/** True if the player is holding the duck / drop-through direction. */
function isDownHeld(p: PlayerLike): boolean {
  const h = p as PlayerHooks
  if (typeof h.input?.down === 'boolean') return h.input.down
  if (typeof h.intent?.down === 'boolean') return h.intent.down
  return false
}

/**
 * True when Tavi is big enough to smash bricks.
 *
 * `PlayerLike.power` is the documented contract and is checked first. The
 * concrete player also carries a wider `state` (which adds `'fire'`), so that
 * is consulted too — `power` is the narrower two-state mirror of it.
 */
function isBig(p: PlayerLike): boolean {
  if (p.power === 'big') return true
  const state = (p as PlayerHooks).state
  return state === 'big' || state === 'fire'
}

/** True while a prism (or post-hit invulnerability window) makes contact safe. */
function isInvincible(p: PlayerLike): boolean {
  const h = p as PlayerHooks
  if (h.isInvulnerable === true) return true
  if (h.isShielded === true) return true
  if (typeof h.shieldTime === 'number' && h.shieldTime > 0) return true
  return typeof h.invuln === 'number' && h.invuln > 0
}

/** Award score through the level, tolerating a runtime without scoring. */
function score(ctx: BehaviourContext, points: number): void {
  lvl(ctx).addScore?.(points)
}

/** Toast above the placement, or the player if the object cannot be found. */
function popupAt(
  ctx: BehaviourContext,
  text: string,
  colour = '#fcd800',
): void {
  const o = self(ctx)
  if (o !== null) {
    const b = boxOf(o)
    spawnHudPopup(b.x + b.w / 2, b.y, text, colour)
    return
  }
  const p = ctx.player
  spawnHudPopup(p.x + p.w / 2, p.y, text, colour)
}

/**
 * Flip a `?`-style block from "armed" to "spent".
 *
 * `spent` is the flag the renderer keys off to swap in the used-block sprite,
 * and it means *exhausted*: a block that still holds payouts keeps the fresh
 * sprite, however many times it has been bumped.
 */
function markSpent(ctx: BehaviourContext): void {
  ctx.state.spent = true
}

/** True if this block has paid out everything it holds. */
function isSpent(ctx: BehaviourContext): boolean {
  return ctx.state.spent === true
}

/**
 * Drive a one-shot visual flash. `state[key]` stays true while the countdown
 * in `state[key + 'Frames']` runs, and the countdown ticks down every update.
 */
function tickFlash(ctx: BehaviourContext, key: string): void {
  const left = num(ctx.state[`${key}Frames`], 0)
  if (left <= 0) return
  const next = left - 1
  ctx.state[`${key}Frames`] = next
  ctx.state[key] = next > 0
}

/**
 * Count the shared `cooldown` counter down. Both one-shot triggers in this file
 * that must not be re-armed by standing on them — the switch and the spring —
 * use this key, so a behaviour that needs a cooldown only has to name it once.
 */
function tickCooldown(ctx: BehaviourContext): void {
  const cd = num(ctx.state.cooldown, 0)
  if (cd > 0) ctx.state.cooldown = cd - 1
}

// ── tuning ──────────────────────────────────────────────────────────────

/**
 * Point values for everything this file pays out.
 *
 * Exported because the block-spawned pickups in `entities.ts` are the *same*
 * items arriving by a different route, and they must be worth the same: an
 * expansion-cell released by a crate block and one placed on the floor are one
 * pickup as far as the player is concerned. Two copies of these numbers would be
 * two chances to drift.
 */
export const SCORE = {
  brickBump: 50,
  coin: 200,
  /**
   * An enemy the player killed.
   *
   * 200, and deliberately not the 100 the original games pay for a stomped
   * rivet. The reason is the other kill route this game already has: touching
   * an enemy while a Shield Prism is running pays 200 (`App.updatePlay`, in
   * `main.ts`), and a stomp is the *intentional* half of the pair — brushing
   * past something invulnerable should not out-earn planting Tavi on its head.
   * 200 is also the rung `coin` and `noteBlock` already sit on, so the score
   * moves in steps a player can recognise.
   *
   * It lives here, with the block and pickup payouts, because every way of
   * killing an enemy is the same event: a stomp, a shell bouncing into
   * something and a prism touching it all remove one enemy and must therefore pay
   * one number. The originals scale this by how many enemies are chained in a
   * row; that is a per-level combo counter and this game has no counter to hang
   * it on yet, so a flat value is the honest simplification.
   */
  stomp: 200,
  powerup: 1000,
  noteBlock: 200,
  checkpoint: 500,
} as const

/**
 * Frames of invincibility a prism grants (4 seconds at 60Hz). Exported for the
 * same reason as `SCORE`: the `?`-block prism is this prism.
 */
export const SHIELD_FRAMES = 4 * 60

/** Frames a switch ignores further contacts, so leaning on it cannot spam. */
const SWITCH_COOLDOWN = 20

/** Launch speeds for a note block: ducking on it, versus walking into it. */
const NOTE_HIGH = -13
const NOTE_LOW = -9

/** How long a spring stays squashed, and a cannon stays flashed, after firing. */
const SPRING_COMPRESS_FRAMES = 6
const CANNON_FLASH_FRAMES = 8

/**
 * Fallbacks for a travelling object that names no `range` / `speed` / `spin`.
 *
 * They exist because `rideTrack` is shared by four ids and only two of the
 * props are always present: the registry defaults cover every id that declares
 * them, so these numbers are reached only by a placement that deliberately
 * overrides a prop with something of the wrong type, or by a def added later
 * without them. 4 tiles and 0.4px/frame is the saw's own default rather than a
 * middle-of-the-road compromise, so a half-configured object behaves like the
 * one it was probably meant to be; 0.3 deg/frame turns a blade once every 3.3
 * seconds, which is the unhurried rate the two saws ship with.
 */
const TRACK_RANGE = 4
const TRACK_SPEED = 0.4
const TRACK_SPIN = 0.3

// ── ? blocks ────────────────────────────────────────────────────────────

/** What a bumped block releases, and what that is worth. */
interface BlockSpec {
  /** Entity kind pushed out of the block. */
  kind: string
  /** Coins added to the counter. */
  coins: number
  /** Score awarded. */
  points: number
}

/**
 * The `?`-block bump: pay out the payload named by the placement's `contains`
 * prop, falling back to the def's own default when the prop is absent or
 * unusable.
 *
 * The prop is read at bump time rather than baked into a registry entry at load,
 * which is what lets one `crate` id cover every payload the palette can load,
 * and keeps the choice visible in the level file where the author wrote it. A
 * def that names its own default (`crate-prism`, and the placements `courses.ts`
 * authors) keeps it: `o.def.props` is the registry's answer, which is what
 * "using the default instead" has to mean. Falling back to a coin there would
 * quietly downgrade a block whose whole identity is its payload.
 */
function blockBumpFromProp(ctx: BehaviourContext): void {
  const o = self(ctx)
  if (o === null) {
    payBlock(ctx, COIN_BLOCK)
    return
  }
  const raw = o.props['contains']
  const spec = contentSpec(raw)
  if (spec !== null) {
    payBlock(ctx, spec)
    return
  }
  // An absent prop is a legitimate authoring choice — every def that reads
  // `contains` declares a default for it, and a placement that overrides it
  // with nothing at all still merges that default — so only a prop that is
  // *present and unrecognised* is reported. A typo (`spare-cell`, `embercell`)
  // used to be indistinguishable from a deliberate coin block: the block paid
  // out 200 points and a coin instead of the prism the course is balanced
  // around, and nothing anywhere said so. `warnOnce` keeps that to one line
  // even though a block with several payouts is bumped several times.
  if (raw !== undefined) {
    warnBadProp(ctx, o, 'contains', raw, Object.keys(BLOCK_CONTENTS).join(', '))
  }
  payBlock(ctx, contentSpec(o.def.props?.['contains']) ?? COIN_BLOCK)
}

/**
 * Map a `contains` prop onto a payload, or null when the value names nothing.
 *
 * `unknown` is deliberate: the prop comes from designer-authored level JSON, and
 * the value is typed `number | string | boolean | undefined` by the placement
 * contract, so it is validated here rather than cast. Nothing is thrown and
 * nothing is invented — a level file is input, and a bad line in it should
 * produce a console warning and a working course rather than a crash on the
 * first head bump.
 */
function contentSpec(raw: unknown): BlockSpec | null {
  if (typeof raw !== 'string') return null
  return BLOCK_CONTENTS[raw] ?? null
}

/**
 * Frames a counted block ignores further head bumps after a payout.
 *
 * A head bump is re-dispatched on every frame Tavi's head is under the block
 * (see `Player.dispatchHeadBump`), so without a latch a block holding three
 * payouts would empty in three frames of one jump. Ten frames is shorter than
 * any real second approach — the player has to fall, land and jump again — so a
 * deliberate re-bump is never swallowed, and long enough that holding jump
 * against a loaded block is not a way to strip it in one press.
 */
const BLOCK_REARM_FRAMES = 10

/**
 * Payouts this block has left, including the one about to happen.
 *
 * Read from `state` once a counted block has paid, so the countdown survives
 * between frames, and from the placement's `count` prop before the first
 * payout. A prop that cannot mean anything is reported and treated as one use;
 * a prop that is merely out of range is clamped by `blockUseCount`.
 */
function remainingUses(ctx: BehaviourContext, o: ObjectLike): number {
  const held = num(ctx.state.uses, 0)
  if (held > 0) return held
  const count = blockUseCount(o.props['count'])
  if (count === null) {
    warnBadProp(
      ctx,
      o,
      'count',
      o.props['count'],
      `a whole number 1-${MAX_BLOCK_CONTENTS}`,
    )
    return 1
  }
  return count
}

/** Release one re-arm latch step, re-arming the block when it expires. */
function rearmBlock(ctx: BehaviourContext): void {
  const left = num(ctx.state.rearm, 0)
  if (left <= 0) return
  const next = left - 1
  ctx.state.rearm = next
  if (next <= 0) ctx.state.bumped = false
}

/**
 * Spend one payout and release `spec` out of the block's top face.
 *
 * A block holds `count` payouts (one by default), so a counted block is spent
 * only when the last one leaves. `revealed` records that the block has been hit
 * at all, which is the whole of a hidden cache's visibility rule.
 */
function payBlock(ctx: BehaviourContext, spec: BlockSpec): void {
  if (isSpent(ctx)) return
  if (ctx.state.bumped === true) return
  const o = self(ctx)
  if (o === null) return

  const held = remainingUses(ctx, o)
  ctx.state.bumped = true
  ctx.state.rearm = BLOCK_REARM_FRAMES
  ctx.state.revealed = true
  if (held <= 1) markSpent(ctx)
  else ctx.state.uses = held - 1

  const b = boxOf(o)
  // `released` tells the payload it came out of a block rather than being
  // placed by a designer. The walking payouts read it so a cell that lands on a
  // shelf steps off instead of patrolling it, which is what puts the payout
  // back within reach of the player standing under the block.
  ctx.level.spawn(spec.kind, b.x + b.w / 2, b.y, { released: true })
  for (let i = 0; i < spec.coins; i++) ctx.level.addCoin()
  score(ctx, spec.points)
  if (spec.coins > 0) {
    sfxCoin()
    spawnHudPopup(b.x + b.w / 2, b.y, `+${spec.points}`, '#fcd800')
  }
}

const COIN_BLOCK: BlockSpec = { kind: 'coin-pop', coins: 1, points: SCORE.coin }
const EXPANSION_BLOCK: BlockSpec = {
  kind: 'expansion-cell',
  coins: 0,
  points: SCORE.powerup,
}
const EMBER_BLOCK: BlockSpec = {
  kind: 'ember-cell',
  coins: 0,
  points: SCORE.powerup,
}
const PRISM_BLOCK: BlockSpec = {
  kind: 'prism',
  coins: 0,
  points: SCORE.powerup,
}
const BATTERY_BLOCK: BlockSpec = {
  kind: 'spare-battery',
  coins: 0,
  points: SCORE.powerup,
}

/**
 * The `contains` prop -> payload table.
 *
 * `ember` is spelled `ember` in the palette and level files but spawns the
 * `ember-cell` entity, which is the only spelling the entity registry knows.
 * A def's registry default is read through this same table (`crate-prism`
 * defaults to `prism`), so a dedicated crate and a plain crate loaded with a
 * prism pay the identical payload.
 */
const BLOCK_CONTENTS: Readonly<Record<string, BlockSpec>> = {
  coin: COIN_BLOCK,
  'expansion-cell': EXPANSION_BLOCK,
  ember: EMBER_BLOCK,
  prism: PRISM_BLOCK,
  'spare-battery': BATTERY_BLOCK,
}

// ── pickups ─────────────────────────────────────────────────────────────

/**
 * Build the collect-on-touch behaviour for a placed pickup.
 *
 * Both hooks are wired up: `update` catches the object layer running on its
 * own, `onSideHit` catches the player controller's own overlap pass. They share
 * one body and are idempotent through `state.collected`, so whichever fires
 * first wins and the other is a no-op.
 */
function pickup(take: (ctx: BehaviourContext) => void): ObjectBehaviour {
  const attempt = (ctx: BehaviourContext): void => {
    if (ctx.state.collected === true) return
    const o = self(ctx)
    if (o === null) return
    if (!ctx.player.alive) return
    if (!overlaps(playerBox(ctx.player), boxOf(o))) return
    ctx.state.collected = true
    take(ctx)
  }
  return { update: attempt, onSideHit: attempt }
}

/** Hand the player fire power, falling back to growing if unsupported. */
function giveFire(ctx: BehaviourContext): void {
  const h = ctx.player as PlayerHooks
  if (typeof h.makeFire === 'function') h.makeFire()
  else if (ctx.player.power === 'small') ctx.player.grow()
}

// ── the behaviour table ─────────────────────────────────────────────────

/**
 * Every id here must exist in `OBJECT_DEFS`. `Level` looks a placement's
 * behaviour up by id when the course loads, so a missing entry simply means an
 * inert object.
 */
export const OBJECT_BEHAVIOURS: Record<string, ObjectBehaviour> = {
  // ── blocks ─────────────────────────────────────────────────────────────

  crate: {
    onHeadBump: blockBumpFromProp,
    update: rearmBlock,
  },

  'crate-expansion-cell': {
    onHeadBump: blockBumpFromProp,
    update: rearmBlock,
  },

  'crate-ember-cell': {
    onHeadBump: blockBumpFromProp,
    update: rearmBlock,
  },

  'crate-prism': {
    onHeadBump: blockBumpFromProp,
    update: rearmBlock,
  },

  /**
   * Invisible until bumped, then behaves exactly like a `?` block — including
   * honouring `contains`, because `courses.ts` authors the payload on hidden
   * blocks as well as on visible ones (three placements carry a Spare Battery or an
   * Expansion Cell this way). A hidden block with no prop is a coin block, as before.
   * It stays revealed between payouts; only `spent` swaps the sprite.
   */
  'hidden-cache': {
    onHeadBump: blockBumpFromProp,
    update: rearmBlock,
  },

  /**
   * Big Tavi smashes the brick; small Tavi only rattles it, which keeps the
   * "grow first" lesson intact. The wreck is four tumbling shards.
   */
  brick: {
    onHeadBump(ctx: BehaviourContext): void {
      const o = self(ctx)
      if (o === null) return
      if (!isBig(ctx.player)) {
        score(ctx, SCORE.brickBump)
        return
      }
      // Guard: `removeObject` splices by placement index, so a second call for
      // an already-removed brick would take out a different object.
      if (ctx.state.bumped === true) return
      ctx.state.bumped = true
      markSpent(ctx)

      const idx = ownIndex(ctx)
      if (idx < 0) return
      const b = boxOf(o)
      const cx = b.x + b.w / 2
      const cy = b.y + b.h / 2
      ctx.level.removeObject(idx)

      // Two shards each way, with alternating tumble so the pile looks loose.
      const shards = [
        { vx: -0.9, spin: 6 },
        { vx: 0.9, spin: -5 },
        { vx: -0.5, spin: 8 },
        { vx: 1.2, spin: -7 },
      ]
      for (const shard of shards) {
        ctx.level.spawn('debris', cx, cy, { vx: shard.vx, spin: shard.spin })
      }
      score(ctx, SCORE.brickBump * 2)
    },
  },

  /**
   * A note block is a trampoline: ducking on it (or holding down as you land)
   * launches Tavi far higher than walking into it does.
   */
  'note-block': {
    onHeadBump(ctx: BehaviourContext): void {
      ctx.player.setVy(isDownHeld(ctx.player) ? NOTE_HIGH : NOTE_LOW)
      score(ctx, SCORE.noteBlock)
    },
  },

  // ── on/off blocks, driven by the level-wide switch ─────────────────────

  /**
   * `onoff` and `onoff-block` are the same rule, so they share one body: the
   * switch is global, and one press governs every instance in the course.
   */
  onoff: {
    update: syncOnOff,
  },
  'onoff-block': {
    update: syncOnOff,
  },

  /**
   * Flips every On/Off block in the course. Triggered by a side hit or by the
   * object layer noticing the player has walked into it; the cooldown means
   * standing against the switch cannot machine-gun the level.
   */
  switch: {
    update(ctx: BehaviourContext): void {
      const o = self(ctx)
      if (o === null) return
      const lv = lvl(ctx)
      // Adopt the level's switch state on the first tick, so the block draws in
      // its "off" sprite from frame one rather than after the first press.
      if (typeof ctx.state.on !== 'boolean')
        ctx.state.on = lv.onOffActive === true

      tickCooldown(ctx)

      const touching =
        ctx.player.alive && overlaps(playerBox(ctx.player), boxOf(o)) // Edge-triggered: only the frame contact *begins* counts as a press.
      if (touching && ctx.state.touching !== true) flipSwitch(ctx)
      ctx.state.touching = touching
    },

    onSideHit(ctx: BehaviourContext): void {
      flipSwitch(ctx)
      ctx.state.touching = true
    },
  },

  // ── interactive ────────────────────────────────────────────────────────

  /**
   * Catches a player who is falling onto the coil and throws them into the sky.
   *
   * The gap is measured from `surfaceY` — the face the player actually lands on
   * — and the window is deliberately asymmetric: a couple of pixels of
   * penetration is fine (tile collision resolves before object updates run, so a
   * landing arrives already snapped to the top with its velocity zeroed), while
   * several pixels of clearance above still counts as a catch at terminal fall
   * speed. Measuring it from the hitbox band instead put the top of the window
   * 8px below where a standing player is, so the spring never fired.
   *
   * It is a latch rather than a plain window so that one landing is one bounce:
   * a cooldown alone would still re-fire while Tavi stood on the coil once it
   * ran out, whereas re-arming only when he leaves the face means the coil has
   * to be stepped off and landed on again.
   */
  spring: {
    update(ctx: BehaviourContext): void {
      const o = self(ctx)
      if (o === null) return
      tickFlash(ctx, 'compress')

      const p = ctx.player
      if (!p.alive) return
      // Rising through the spring from below must never launch.
      if (p.vy < 0) return

      // One predicate for "on the face", because the re-arm has to be the
      // *negation* of it. Testing the two axes in sequence — return early when
      // the player is beside the coil, re-arm when the gap is wrong — left a
      // player who stepped sideways off a spent coil with it still shut, and a
      // spring that fired once and never again.
      const b = boxOf(o)
      const overX = p.x < b.x + b.w && p.x + p.w > b.x
      const gap = surfaceY(o) - (p.y + p.h)
      if (!overX || gap < -2 || gap > 6) {
        ctx.state.armed = true
        return
      }
      // `=== false` rather than `!== true`, so a spring nobody has landed on yet
      // counts as armed. Shutting after a launch and re-arming only on the way
      // out is what makes this one bounce per landing, and any bounce at all
      // clears the window, so coming back down is a fresh landing.
      if (ctx.state.armed === false) return
      ctx.state.armed = false

      ctx.state.compress = true
      ctx.state.compressFrames = SPRING_COMPRESS_FRAMES
      p.setVy(PHYS.springBounce)
    },
  },

  /** Fires a fireball down its barrel, staggered so a row of cannons ripples. */
  cannon: {
    update(ctx: BehaviourContext): void {
      const o = self(ctx)
      if (o === null) return
      tickFlash(ctx, 'firing')

      const p = ctx.player
      if (!p.alive) return

      // Two seconds between shots, which is the cadence the original games use
      // for a cannon: slow enough that a row of them is a rhythm to be walked
      // between rather than a wall, fast enough that a player standing in front
      // of one is never waiting on it. The stagger below is what stops a row
      // firing in lockstep.
      const period = 120
      // Stagger by position: cannons a few tiles apart never fire in lockstep.
      const phase = (o.tx * 7 + o.ty * 13) % period
      if ((ctx.tick + phase) % period !== 0) return

      const b = boxOf(o)
      const muzzleX = b.x + b.w + 1
      const muzzleY = b.y + b.h / 2

      const dx = p.x + p.w / 2 - muzzleX
      // In front of the barrel, within ten tiles…
      if (dx < -TILE / 2 || dx > 10 * TILE) return
      // …and roughly level with it, so a cannon does not shoot at the sky.
      if (Math.abs(p.y + p.h / 2 - muzzleY) > 4 * TILE) return

      ctx.level.spawn('fireball', muzzleX, muzzleY, { facing: 1 })
      ctx.state.firing = true
      ctx.state.firingFrames = CANNON_FLASH_FRAMES
    },
  },

  /** Raises the flag and moves the respawn point, once. */
  checkpoint: {
    update: takeCheckpoint,
    onSideHit: takeCheckpoint,
  },

  /** Touch the goal and the course is cleared. */
  goal: {
    update(ctx: BehaviourContext): void {
      if (ctx.state.taken === true) return
      const o = self(ctx)
      if (o === null) return
      if (!ctx.player.alive) return
      if (!overlaps(playerBox(ctx.player), boxOf(o))) return
      ctx.state.taken = true
      ctx.level.finish()
    },
  },

  /**
   * A vine claims Tavi while he is inside it: the flag is what the player
   * controller reads to know he has grabbed on, and zeroing velocity is what
   * keeps him pinned instead of sliding off.
   */
  vine: {
    update(ctx: BehaviourContext): void {
      const o = self(ctx)
      if (o === null) return
      if (grabVine(ctx, o)) return
      // Off the vine again: drop the flag so the controller stops climbing.
      if (ctx.state.climbing === true) ctx.state.climbing = false
    },
    onSideHit(ctx: BehaviourContext): void {
      const o = self(ctx)
      if (o === null) return
      grabVine(ctx, o)
    },
  },

  // ── hazards ────────────────────────────────────────────────────────────

  /**
   * Shared by every instant-death surface. A prism makes Tavi pass through,
   * which is the entire point of the prism.
   */
  spike: {
    update: killOnContact,
  },
  lava: {
    update: killOnContact,
  },

  /**
   * A saw rides a fixed track, ping-ponging between its placed position and
   * `range` tiles away. `saw` runs vertically, `saw-h` horizontally.
   */
  saw: {
    update(ctx: BehaviourContext): void {
      rideTrack(ctx, 'vertical')
      killOnContact(ctx)
    },
  },
  'saw-h': {
    update(ctx: BehaviourContext): void {
      rideTrack(ctx, 'horizontal')
      killOnContact(ctx)
    },
  },

  // ── items ──────────────────────────────────────────────────────────────

  coin: pickup((ctx) => {
    ctx.level.addCoin()
    score(ctx, SCORE.coin)
    sfxCoin()
    popupAt(ctx, `+${SCORE.coin}`, '#fcd800')
  }),

  'expansion-cell': pickup((ctx) => {
    // An expansion-cell for a big Tavi is worth points, not a second power-up.
    if (!isBig(ctx.player)) ctx.player.grow()
    score(ctx, SCORE.powerup)
    sfxPower()
    popupAt(ctx, `+${SCORE.powerup}`, '#fcfcfc')
  }),

  'ember-cell': pickup((ctx) => {
    giveFire(ctx)
    score(ctx, SCORE.powerup)
    sfxPower()
    popupAt(ctx, `+${SCORE.powerup}`, '#fcfcfc')
  }),

  prism: pickup((ctx) => {
    const h = ctx.player as PlayerHooks
    if (typeof h.grantShield === 'function') h.grantShield(SHIELD_FRAMES)
    else h.shieldTime = SHIELD_FRAMES
    score(ctx, SCORE.powerup)
    sfxPower()
    popupAt(ctx, `+${SCORE.powerup}`, '#fcfcfc')
  }),

  'spare-battery': pickup((ctx) => {
    addLife(ctx)
    score(ctx, SCORE.powerup)
    sfxPower()
    popupAt(ctx, '1UP', '#85ded1')
  }),

  // ── moving platforms ──────────────────────────────────────────────────
  //
  // `rideTrack` was written alongside the rest of the behaviours but never
  // registered, so every `moving-platform` in the shipped courses sat inert.
  // That made the level author's 15-tile pit in Pipe Dreams and the lift in
  // Star Ride impassable, and the goal in Star Ride unreachable.

  'moving-platform': {
    update(ctx) {
      rideTrack(ctx, 'vertical')
    },
  },

  'moving-platform-h': {
    update(ctx) {
      rideTrack(ctx, 'horizontal')
    },
  },
}

// ── behaviour bodies, factored out of the table above ───────────────────

/**
 * Object solidity is baked into the merged collision grid at load, so the only
 * way to make an On/Off block non-solid is to rewrite the terrain underneath
 * it. The original tile is remembered on the first tick and restored whenever
 * the switch comes back on; while the switch is off the tile is air and the
 * player falls straight through.
 */
function syncOnOff(ctx: BehaviourContext): void {
  const o = self(ctx)
  if (o === null) return
  const lv = lvl(ctx)
  if (lv.setTerrain === undefined) return

  if (typeof ctx.state.prev !== 'number') {
    ctx.state.prev = lv.terrainAt?.(o.tx, o.ty) ?? Terrain.EMPTY
  }
  const active = lv.onOffActive === true
  // `on` is both the change guard and the flag the renderer draws from.
  if (ctx.state.on === active) return
  ctx.state.on = active

  const prev = num(ctx.state.prev, Terrain.EMPTY)
  // Re-activating a block that replaced bare air still has to be solid, so
  // fall back to GROUND rather than restoring a non-solid terrain id.
  //
  // That fallback has one consequence worth naming, because it is the only
  // coercion in this file that destroys authored information: a one-way platform
  // under an On/Off block (`Terrain.PLATFORM` is the sole non-solid terrain id
  // that is not air) comes back as full ground, and the player can no longer
  // drop through it. `setTerrain` can only write one id per tile and derives the
  // collision grids from it, so "one-way *and* currently driven by a full-solid
  // object" is not representable — the choice is between losing the platform and
  // leaving the block un-solid. Losing it is the right way round; the warning
  // is what keeps it from being silent. Air under the block is the normal case
  // and is deliberately not reported, or every on/off block in every course
  // would print a line.
  const restore = SOLID_TERRAIN.has(prev) ? prev : Terrain.GROUND
  if (prev === Terrain.PLATFORM) {
    warnOnce(
      ctx,
      'terrain',
      `behaviour: on/off block at ${o.tx},${o.ty} stands on a one-way ` +
        `platform tile; the block can only be air or solid, so the platform ` +
        `is replaced by ground and cannot be restored`,
    )
  }
  const next = active ? restore : Terrain.EMPTY

  for (let dy = 0; dy < o.def.h; dy++) {
    for (let dx = 0; dx < o.def.w; dx++) {
      lv.setTerrain(o.tx + dx, o.ty + dy, next)
    }
  }
}

/** Invert the level-wide On/Off switch, honouring the press cooldown. */
function flipSwitch(ctx: BehaviourContext): boolean {
  const lv = lvl(ctx)
  const cd = num(ctx.state.cooldown, 0)
  if (cd > 0) return false
  ctx.state.cooldown = SWITCH_COOLDOWN
  lv.onOffActive = !lv.onOffActive
  ctx.state.on = lv.onOffActive
  ctx.state.pressed = lv.onOffActive
  return true
}

/** Take the checkpoint if it has not been taken, and Tavi is touching it. */
function takeCheckpoint(ctx: BehaviourContext): void {
  if (ctx.state.taken === true) return
  const o = self(ctx)
  if (o === null) return
  if (!ctx.player.alive) return
  if (!overlaps(playerBox(ctx.player), boxOf(o))) return
  ctx.state.taken = true
  lvl(ctx).setCheckpoint?.(o.tx, o.ty)
  score(ctx, SCORE.checkpoint)
  sfxCheckpoint()
  popupAt(ctx, `+${SCORE.checkpoint}`, '#85ded1')
}

/** Pin the player to a vine. Returns true while he is on it. */
function grabVine(ctx: BehaviourContext, o: ObjectLike): boolean {
  const p = ctx.player
  if (!p.alive || !overlaps(playerBox(p), boxOf(o))) return false
  ctx.state.climbing = true
  p.vx = 0
  p.vy = 0
  return true
}

/** Kill the player on contact, unless a prism is making him untouchable. */
function killOnContact(ctx: BehaviourContext): void {
  const o = self(ctx)
  if (o === null) return
  const p = ctx.player
  if (!p.alive) return
  if (isInvincible(p)) return
  if (!overlaps(playerBox(p), boxOf(o))) return
  p.kill()
}

/**
 * Move a saw along its track. The phase is a monotonically increasing frame
 * counter folded into a triangle wave, so a saw resumes exactly where it was
 * and any number of them stay out of step.
 */
function rideTrack(
  ctx: BehaviourContext,
  axis: 'vertical' | 'horizontal',
): void {
  const o = self(ctx)
  if (o === null) return

  // The three props this reads, and what each one is for. `range` is a distance
  // in tiles, `speed` is px per frame along that distance, and `spin` is the
  // blade's rotation rate in degrees per frame — cosmetic, and read by the
  // renderer through `state.spin`, which is why a `moving-platform` carrying it
  // sees no effect: only the saw sprites are rotated.
  //
  // Every one of the three is clamped rather than trusted, and each clamp is
  // there because the un-clamped value is worse than the fallback:
  //
  //   - a negative `range` would travel *away* from the post forever, because
  //     `tri * range` goes negative and nothing reflects it. Zero pins the
  //     object where it was placed, which is inert but at least local.
  //   - a zero or negative `speed` divides the period to zero or to a negative
  //     count of frames. 0.05 keeps the arithmetic finite and still moves.
  //   - `spin` needs no clamp. A negative rate simply turns the blade the other
  //     way, and that is a legible request rather than a mistake.
  //
  // The first two are also *reported*, because a saw that quietly stops moving
  // looks like a broken game rather than a broken level: the level is
  // unplayable and the two plausible causes (a bad prop, or a hazard that
  // simply is not animated) are indistinguishable on screen.
  const range = Math.max(0, num(o.props['range'], TRACK_RANGE)) * TILE
  const speed = Math.max(0.05, num(o.props['speed'], TRACK_SPEED))
  const spinRate = num(o.props['spin'], TRACK_SPIN)
  if (typeof o.props['range'] === 'number' && o.props['range'] < 0) {
    warnBadProp(ctx, o, 'range', o.props['range'], 'a distance in tiles, >= 0')
  }
  if (typeof o.props['speed'] === 'number' && o.props['speed'] <= 0) {
    warnBadProp(ctx, o, 'speed', o.props['speed'], 'a positive px/frame rate')
  }

  const phase = num(ctx.state.phase, 0) + 1
  ctx.state.phase = phase

  // Frames for a full there-and-back traverse, so the cycle is a whole number
  // of frames and the saw never jitters on the end stops.
  const period = Math.max(2, Math.round((range * 2) / speed))
  const t = (phase % period) / period
  const tri = t < 0.5 ? t * 2 : (1 - t) * 2
  const travel = tri * range

  const ox = axis === 'horizontal' ? travel : 0
  const oy = axis === 'vertical' ? -travel : 0

  o.ox = ox
  o.oy = oy
  ctx.state.spin = num(ctx.state.spin, 0) + spinRate
}

/** Add a life, preferring the level's own counter. */
function addLife(ctx: BehaviourContext): void {
  const stats = lvl(ctx).stats
  if (stats !== undefined) {
    stats.lives += 1
    return
  }
  const h = ctx.player as PlayerHooks
  if (typeof h.lives === 'number') h.lives += 1
}
